<template>
  <section class="bg-white p-3 border border-gray-200 rounded-md">
    <div class="flex items-center justify-between gap-1.5 mb-2">
      <div class="flex items-center gap-1.5">
        <i class="pi pi-list text-purple-500 text-sm"></i>
        <h3 class="font-semibold text-sm text-gray-700">Enrolled {{ labels.SUBJECT }}s</h3>
      </div>
      <Button :label="`Enroll ${labels.SUBJECT}s`" icon="pi pi-plus" size="small" @click="openEnrollDialog" class="p-button-sm" />
    </div>

    <div v-if="loading" class="flex justify-center py-2">
      <i class="pi pi-spin pi-spinner text-lg text-gray-400"></i>
    </div>

    <div v-else-if="subjects.length" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
      <div v-for="subject in subjects" :key="subject.id"
        class="p-2 border rounded hover:shadow-sm bg-gray-50 flex items-center justify-between">
        <div>
          <div class="font-medium text-xs text-gray-800">{{ subject.name }}</div>
          <div class="text-[11px] text-gray-500">{{ subject.code }}</div>
        </div>
        <!-- Unenroll button -->
        <button @click="confirmUnenroll(subject)" class="text-red-500 hover:text-red-700 p-1.5 rounded-full hover:bg-red-50 transition" title="Unenroll" data-testid="btn-unenroll-subject">
          <i class="pi pi-trash text-xs"></i>
        </button>
      </div>
    </div>

    <div v-else class="text-gray-500 italic text-xs">
      No {{ labels.SUBJECT.toLowerCase() }}s enrolled.
    </div>

    <Dialog v-model:visible="enrollDialogVisible" :header="`Enroll Student in ${labels.SUBJECT}s`" modal
      class="w-[90%] md:w-[500px]">
      <div class="flex flex-col gap-2">
        <div v-if="loadingAllSubjects" class="flex justify-center p-2">
          <i class="pi pi-spin pi-spinner text-lg text-gray-400"></i>
        </div>
        <div v-else class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-gray-700">Select {{ labels.SUBJECT }}s</label>
          <MultiSelect v-model="selectedSubjects" :options="availableSubjects" optionLabel="name" optionValue="id"
            :placeholder="`Select ${labels.SUBJECT.toLowerCase()}s`" filter class="w-full" display="chip" />
          <small class="text-gray-500 text-xs" v-if="availableSubjects.length === 0">No new {{ labels.SUBJECT.toLowerCase() }}s
            available to
            enroll.</small>
        </div>
      </div>
      <template #footer>
        <Button label="Cancel" text size="small" @click="enrollDialogVisible = false" />
        <Button label="Enroll" icon="pi pi-check" size="small" @click="enrollSubjects" :loading="enrolling"
          :disabled="!selectedSubjects.length" />
      </template>
    </Dialog>
  </section>
</template>

<script setup lang="ts">
import { useSchoolLabels } from '@/composables/useSchoolLabels';
import { useQuery } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import { EnrollmentModel, DeletableModelType } from '@/types/composition-functions';
import type { Subject } from '@/types/composition-functions';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';

import { useEnrollStudentSubjectMutation, useDeleteSubjectEnrollmentMutation, useGetAllSubjectsQuery } from '@/types/vue-apollo-helpers';

const { labels } = useSchoolLabels();

const props = defineProps<{
  subjects: { id: string; name: string; code?: string }[];
  loading: boolean;
}>();

const route = useRoute();
const toast = useToast();
const confirm = useConfirm();
const studentId = Number(route.params.id);

const enrollDialogVisible = ref(false);
const selectedSubjects = ref<string[]>([]);

// Get active academic calendar
const { activeAcademicCalendar } = useSchoolQuery();

// Fetch all subjects
const { result: allSubjectsResult, loading: loadingAllSubjects } = useGetAllSubjectsQuery({
  fetchPolicy: 'cache-and-network',
});

const allSubjects = computed(() => allSubjectsResult.value?.listSchoolSubjects || []);

// Filter out subjects the student is already enrolled in
const availableSubjects = computed(() => {
  const enrolledIds = new Set(props.subjects.map((s) => s.id));
  return allSubjects.value.filter(
    (s): s is { id: string; name: string } => !!s && !enrolledIds.has(s.id),
  );
});

const openEnrollDialog = () => {
  enrollDialogVisible.value = true;
  selectedSubjects.value = [];
};

const { mutate: enrollStudentSubject, loading: enrolling } = useEnrollStudentSubjectMutation();
const { mutate: deleteSubjectEnrollment, loading: unenrolling } = useDeleteSubjectEnrollmentMutation();

const enrollSubjects = async() => {
  if (!selectedSubjects.value.length) return;

  try {
    const result = await enrollStudentSubject({
      model: EnrollmentModel.Student, // Using the enum from types
      ids: [studentId],
      subjectIds: selectedSubjects.value,
      academicCalendarId: activeAcademicCalendar.value?.id,
    });

    if (result?.data?.enrollStudentSubject?.success) {
      toast.add({ severity: 'success', summary: 'Success', detail: result.data.enrollStudentSubject.message, life: 3000 });
      enrollDialogVisible.value = false;
      emit('refresh');
    } else {
      toast.add({ severity: 'error', summary: 'Error', detail: result?.data?.enrollStudentSubject?.message || 'Enrollment failed', life: 3000 });
    }
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: e instanceof Error ? e.message : 'An error occurred', life: 3000 });
  }
};

const confirmUnenroll = (subject: { id: string; name: string }) => {
  if (!activeAcademicCalendar.value?.id) {
    toast.add({ severity: 'warn', summary: 'Warning', detail: 'No active academic calendar found.', life: 3000 });
    return;
  }

  confirm.require({
    message: `Are you sure you want to unenroll this student from ${subject.name}?`,
    header: 'Confirmation',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: async() => {
      try {
        const result = await deleteSubjectEnrollment({
          modelIds: [studentId],
          modelType: DeletableModelType.Student,
          subject_id: subject.id,
          academic_calendar_id: activeAcademicCalendar.value.id,
        });

        if (result?.data?.deleteSubjectEnrollment?.success) {
          toast.add({ severity: 'success', summary: 'Success', detail: 'Student unenrolled successfully', life: 3000 });
          emit('refresh');
        } else {
          toast.add({ severity: 'error', summary: 'Error', detail: result?.data?.deleteSubjectEnrollment?.message || 'Unenrollment failed', life: 3000 });
        }
      } catch (e) {
        toast.add({ severity: 'error', summary: 'Error', detail: e instanceof Error ? e.message : 'An error occurred', life: 3000 });
      }
    },
  });
};

const emit = defineEmits(['refresh']);
</script>
